// components/templates/MultiTemplate.tsx
import React, { useState, useEffect, useRef } from "react";
import {
    MapPin,
    BedDouble,
    Bath,
    Square,
    Upload,
    Image as ImageIcon,
    Phone,
    Mail,
    Building2,
    X,
    Globe,
    ExternalLink,
    Loader2,
    Crop,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { ImageCropModal } from "@/components/ui/ImageCropModal";

// ─── Helper: route already-saved (non data:/blob:) S3 image URLs through the
// backend image proxy before feeding them to the crop modal's <canvas>, so the
// export doesn't fail with a cross-origin "tainted canvas" error. Copied
// verbatim from the single-listing templates' own getEditableImageUrl helper.
const getEditableImageUrl = (url: string): string => {
    if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
    try {
        const parsed = new URL(url, window.location.origin);
        const needsProxy =
            parsed.hostname.includes("amazonaws.com") ||
            parsed.hostname.includes("mylistiq-production.s3");
        if (!needsProxy) return url;
        return `${endpoints.image_proxy}?url=${encodeURIComponent(url)}`;
    } catch {
        return url;
    }
};

// ─── Helper: convert the crop modal's output data URL into a File, the same
// way a freshly-picked photo already becomes a File from a file input.
const dataUrlToFile = (dataUrl: string, filename: string): File => {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
};

interface MultiTemplateProps {
    index: number;
    property: {
        id: string;
        image: string;
        original_image?: string;
        price: number;
        bedrooms: number;
        bathrooms: number;
        area: number;
        address: string;
        city?: string;
        state?: string;
        status?: string;
        description?: string;
        title?: string;
        photos?: string[];
        agentName?: string;
        agentPhone?: string;
        agentEmail?: string;
        agentAvatar?: string;
        companyLogo?: string;
        company?: string;
        website?: string;
    };
    onViewListing?: () => void;
    onContact?: () => void;
    onChange?: (fields: Partial<MultiTemplateData>) => void;
    onImageChange?: (file: File, type: 'main' | 'secondary') => void;
    imageUploading?: boolean;
    readOnly?: boolean;
    getFullUrl?: (url: string | null | undefined) => string;
}

export interface MultiTemplateData {
    price: number;
    bedrooms: number;
    bathrooms: number;
    area: number;
    address: string;
    city?: string;
    state?: string;
    status: string;
    description: string;
    title: string;
    image: string;
    agentName: string;
    agentPhone: string;
    agentEmail: string;
    company: string;
    website: string;
}

const MultiTemplate: React.FC<MultiTemplateProps> = ({
    index,
    property,
    onContact,
    onChange,
    onImageChange,
    imageUploading = false,
    readOnly = false,
    getFullUrl = (url) => url || "",
}) => {
    const { toast } = useToast();
    const mainImageRef = useRef<HTMLInputElement>(null);
    const secondaryImageRef = useRef<HTMLInputElement>(null);

    const previousPropertyId = useRef(property.id);
    const isInitialMount = useRef(true);
    // Local state for editable fields
    const [localData, setLocalData] = useState({
        price: property.price || 0,
        bedrooms: property.bedrooms || 0,
        bathrooms: property.bathrooms || 0,
        area: property.area || 0,
        address: property.address || "",
        status: property.status || "For Sale",
        description: property.description || "",
        title: property.title || "",
        image: property.image || "",
        agentName: property.agentName || "",
        agentPhone: property.agentPhone || "",
        agentEmail: property.agentEmail || "",
        company: property.company || "",
        website: property.website || "",
    });

    const [secondaryImage, setSecondaryImage] = useState<string>(
        property.photos?.[0] || ""
    );

    const websiteTrimmed = (localData.website || "").trim();
    const viewListingHref = websiteTrimmed
        ? websiteTrimmed.startsWith("http")
            ? websiteTrimmed
            : `https://${websiteTrimmed}`
        : "";

    // Sync with props when they change
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        // In readOnly mode, always sync with props
        // In edit mode, only sync if property ID changed
        if (readOnly || previousPropertyId.current !== property.id) {
            previousPropertyId.current = property.id;
            setLocalData({
                price: property.price || 0,
                bedrooms: property.bedrooms || 0,
                bathrooms: property.bathrooms || 0,
                area: property.area || 0,
                address: property.address || "",
                status: property.status || "For Sale",
                description: property.description || "",
                title: property.title || "",
                image: property.image || "",
                agentName: property.agentName || "",
                agentPhone: property.agentPhone || "",
                agentEmail: property.agentEmail || "",
                company: property.company || "",
                website: property.website || "",
            });
            setSecondaryImage(property.photos?.[0] || "");
        }
    }, [property.id, readOnly]);

    // Handle input change
    const handleInputChange = (field: keyof MultiTemplateData, value: any) => {
        if (readOnly) return;

        let normalizedValue = value;

        // Handle numeric fields
        if (["price", "bedrooms", "bathrooms", "area"].includes(field)) {
            if (field === "price") {
                normalizedValue = value === "" ? 0 : Number(String(value).replace(/[^0-9]/g, ""));
            } else {
                normalizedValue = value === "" ? 0 : Number(value);
            }
        }

        setLocalData(prev => ({
            ...prev,
            [field]: normalizedValue,
        }));

        onChange?.({ [field]: normalizedValue });
    };

    // Handle main image upload
    const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            toast({
                title: "Invalid File",
                description: "Please upload an image file",
                variant: "destructive",
            });
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            toast({
                title: "File Too Large",
                description: "Image must be less than 10 MB",
                variant: "destructive",
            });
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            const result = reader.result as string;
            setLocalData(prev => ({ ...prev, image: result }));
            onChange?.({ image: result });
        };
        reader.readAsDataURL(file);

        // A genuinely new photo pick invalidates any previously-captured
        // "original" — otherwise the next Edit click would keep re-opening
        // whatever photo was cropped before this replacement instead of the
        // photo the user just picked. Clearing the local cache here forces
        // openEditImage to re-derive it from the server (which the parent's
        // Save also refreshes to this new file, see MultiPropertyPreviewPage's
        // handleSaveEdit).
        setOriginalImage("");

        onImageChange?.(file, 'main');
        e.target.value = "";
    };

    // =========================================================================
    // EDIT IMAGE (crop/zoom the main photo, non-destructively)
    //
    // This is deliberately independent of the "Change Main Photo" flow above,
    // which only stages a File on the parent (committed later via "Save
    // Changes"). Edit Image instead saves immediately and directly to the
    // backend, exactly like every single-listing template's Edit Image
    // feature, so it can never interact with or be lost by the deferred
    // stage/Save/Cancel flow for the other fields.
    // =========================================================================
    const EDIT_CROP_W = 340;
    const EDIT_CROP_H = 340; // matches the 340x340 main-image box

    const [cropModal, setCropModal] = useState<{ open: boolean; imageUrl: string }>({
        open: false,
        imageUrl: "",
    });
    const [originalImage, setOriginalImage] = useState<string>("");
    const [isSavingEditedImage, setIsSavingEditedImage] = useState(false);

    const openEditImage = async () => {
        if (readOnly || !localData.image) return;

        let originalSource = originalImage || (property as any)?.original_image || "";

        if (!originalSource && property.id) {
            try {
                const res = await axiosInstance.get(`${endpoints.properties}${property.id}/`);
                originalSource = res?.data?.original_image || "";
            } catch (err) {
                console.error("Failed to check for existing original image:", err);
            }
        }

        if (!originalSource) {
            // Truly never captured yet. Self-heal from the current image so this
            // and every future edit has a stable, full source to re-crop from.
            // Skip seeding if the current image is only a local, unsaved preview
            // (data:/blob:) — there's nothing on the server yet to attach it to.
            originalSource = localData.image;
            if (property.id && !localData.image.startsWith("data:") && !localData.image.startsWith("blob:")) {
                axiosInstance
                    .patch(`${endpoints.properties}${property.id}/`, { original_image: localData.image })
                    .catch((err) => {
                        console.error("Failed to seed original image:", err);
                    });
            }
        }

        setOriginalImage(originalSource);
        setCropModal({ open: true, imageUrl: getEditableImageUrl(originalSource) });
    };

    const handleEditImageConfirm = async (croppedDataUrl: string) => {
        setLocalData(prev => ({ ...prev, image: croppedDataUrl }));
        onChange?.({ image: croppedDataUrl });

        if (property.id) {
            setIsSavingEditedImage(true);
            try {
                const file = dataUrlToFile(croppedDataUrl, `edited-property-${Date.now()}.png`);
                const formData = new FormData();
                formData.append("image", file);
                const response = await axiosInstance.patch(
                    `${endpoints.properties}${property.id}/`,
                    formData,
                    { headers: { "Content-Type": undefined as any }, transformRequest: [(data) => data] }
                );
                if (response.data?.image) {
                    setLocalData(prev => ({ ...prev, image: response.data.image }));
                    onChange?.({ image: response.data.image });
                }
            } catch (err) {
                console.error("Failed to save edited image:", err);
                toast({
                    title: "Warning",
                    description: "Image preview updated but saving the crop failed",
                    variant: "destructive",
                    duration: 1500,
                });
            } finally {
                setIsSavingEditedImage(false);
            }
        }

        setCropModal({ open: false, imageUrl: "" });
    };

    const handleEditImageCancel = () => {
        setCropModal({ open: false, imageUrl: "" });
    };

    // Handle secondary image upload
    const handleSecondaryImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            toast({
                title: "Invalid File",
                description: "Please upload an image file",
                variant: "destructive",
            });
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            toast({
                title: "File Too Large",
                description: "Image must be less than 10 MB",
                variant: "destructive",
            });
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            const result = reader.result as string;
            setSecondaryImage(result);
        };
        reader.readAsDataURL(file);

        onImageChange?.(file, 'secondary');
        e.target.value = "";
    };

    // Remove secondary image
    const handleRemoveSecondaryImage = () => {
        setSecondaryImage("");
    };

    // Status options
    const statusOptions = ["For Sale", "For Rent", "Sold", "Pending", "Coming Soon", "Price Reduced"];

    return (
        <div className="w-full">
            <div
                className="template-preview-card bg-white rounded-xl sm:rounded-2xl shadow-lg border mx-auto inline-block align-top overflow-hidden"
                style={{ minWidth: "760px" }}
            >
                {/* Property Number Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 sm:px-6 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-blue-600 font-bold text-lg">
                            {index}
                        </div>
                        <div className="text-white">
                            <p className="text-sm opacity-80">Property</p>
                            {readOnly ? (
                                <p className="font-semibold">{localData.title || localData.address || `Property #${index}`}</p>
                            ) : (
                                <input
                                    type="text"
                                    value={localData.title}
                                    onChange={(e) => handleInputChange("title", e.target.value)}
                                    className="font-semibold bg-transparent border-none outline-none text-white placeholder-white/60 w-full"
                                    placeholder="Property Title"
                                />
                            )}
                        </div>
                    </div>

                    {/* Website Link Indicator in Header */}
                    {/* {localData.website && (
                    <a
                        href={localData.website.startsWith('http') ? localData.website : `https://${localData.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg text-white text-sm transition-colors"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ExternalLink className="h-4 w-4" />
                        <span className="hidden sm:inline">External Link</span>
                    </a>
                )} */}
                </div>

                {/* <div className="grid grid-cols-[minmax(0,1fr)_320px] gap-0 min-w-[640px]"> */}
                <div className="grid grid-cols-[340px_minmax(420px,1fr)] gap-0 min-w-0">
                    {/* Left - Images */}
                    <div className="relative">
                        {/* Main Image */}
                        <div className="relative h-[340px] group"
                            style={{ height: '340px', minHeight: '340px', maxHeight: '340px', width: '340px', minWidth: '340px', maxWidth: '340px' }}
                        >
                            {localData.image ? (
                                <>
                                    <img
                                        src={getFullUrl(localData.image)}
                                        alt="Property"
                                        className="absolute inset-0 w-full h-full object-cover object-center"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = 'none';
                                        }}
                                    />
                                    {imageUploading && (
                                        <div className="absolute inset-0 z-10 bg-black/60 flex flex-col items-center justify-center text-white gap-2">
                                            <Loader2 className="h-8 w-8 animate-spin" />
                                            <p className="text-sm font-semibold">Uploading image…</p>
                                        </div>
                                    )}
                                    {!readOnly && (
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-2">
                                            <Button
                                                size="sm"
                                                onClick={() => !imageUploading && mainImageRef.current?.click()}
                                                className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                                                disabled={imageUploading}
                                            >
                                                <Upload className="h-4 w-4 mr-2" />
                                                Change Main Photo
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => !imageUploading && !isSavingEditedImage && openEditImage()}
                                                className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                                                disabled={imageUploading || isSavingEditedImage}
                                            >
                                                <Crop className="h-4 w-4 mr-2" />
                                                Edit
                                            </Button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <label
                                    className={`absolute inset-0 flex flex-col items-center justify-center bg-gray-100 ${!readOnly ? "cursor-pointer hover:bg-gray-200" : ""} transition-colors`}
                                    onClick={() => !readOnly && mainImageRef.current?.click()}
                                >
                                    <ImageIcon className="h-16 w-16 text-gray-400 mb-3" />
                                    <p className="text-gray-500 font-medium">
                                        {readOnly ? "No Image" : "Upload Main Photo"}
                                    </p>
                                </label>
                            )}

                            <input
                                ref={mainImageRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleMainImageChange}
                                disabled={readOnly || imageUploading}
                            />

                            {!readOnly && (
                                <ImageCropModal
                                    open={cropModal.open}
                                    imageUrl={cropModal.imageUrl}
                                    title="Adjust Main Photo"
                                    outputSize={700}
                                    cropW={EDIT_CROP_W}
                                    cropH={EDIT_CROP_H}
                                    onConfirm={handleEditImageConfirm}
                                    onCancel={handleEditImageCancel}
                                />
                            )}

                            {/* Price Overlay */}
                            <div className="absolute right-3 top-3 max-w-[calc(100%-1.5rem)] rounded-xl bg-yellow-500 px-3 py-1.5 text-white shadow-lg sm:right-4 sm:top-4 sm:px-4 sm:py-2">
                                {readOnly ? (
                                    <p className="text-lg font-bold sm:text-2xl">
                                        ${(localData.price || 0).toLocaleString()}
                                    </p>
                                ) : (
                                    <input
                                        type="text"
                                        value={localData.price ? `$${localData.price.toLocaleString()}` : ""}
                                        onChange={(e) => {
                                            const num = e.target.value.replace(/[^0-9]/g, "");
                                            handleInputChange("price", num);
                                        }}
                                        className="w-28 bg-transparent text-lg font-bold text-white placeholder-white/70 outline-none border-none sm:w-40 sm:text-2xl"
                                        placeholder="$0"
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right - Content */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', padding: '1rem 1.5rem' }}>
                        {/* Address - Single Field Only */}
                        <div>
                            <div className="flex items-start gap-2 mb-4">
                                <MapPin className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                                {readOnly ? (
                                    <p className="font-medium text-gray-900">{localData.address || "Address not available"}</p>
                                ) : (
                                    <input
                                        type="text"
                                        value={localData.address}
                                        onChange={(e) => handleInputChange("address", e.target.value)}
                                        className="font-medium text-gray-900 bg-transparent border-b border-gray-200 outline-none w-full focus:border-blue-500 pb-1 placeholder-gray-400"
                                        placeholder="Full Property Address (e.g., 123 Main St, City, State 12345)"
                                    />
                                )}
                            </div>

                            {/* Features Grid */}
                            {/* Features Grid */}
                            <div className="bg-blue-50 rounded-xl p-4 mb-4">
                                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">
                                    Property Details
                                </p>

                                {/* <div className="flex gap-3"> */}
                                <div className="grid grid-cols-3 gap-3 w-full min-w-0">
                                    {/* Bedrooms */}
                                    {/* <div className="flex-1 min-w-[120px] bg-white rounded-lg p-3 shadow-sm"> */}
                                    {/* <div className="min-w-0 bg-white rounded-lg p-3 shadow-sm overflow-hidden"> */}
                                    <div className="min-w-0 bg-white rounded-lg p-3 shadow-sm">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                                <BedDouble className="h-4 w-4 text-blue-600" />
                                            </div>
                                        </div>

                                        {readOnly ? (
                                            <p className="text-sm sm:text-base md:text-lg font-bold leading-tight text-gray-900 break-words">
                                                {localData.bedrooms}
                                            </p>
                                        ) : (
                                            <input
                                                type="number"
                                                value={localData.bedrooms || ""}
                                                onChange={(e) =>
                                                    handleInputChange("bedrooms", e.target.value)
                                                }
                                                className="text-lg font-bold text-gray-900 bg-transparent border-none outline-none w-full whitespace-nowrap [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                placeholder="0"
                                                min="0"
                                            />
                                        )}

                                        <p className="text-xs text-gray-500">Beds</p>
                                    </div>

                                    {/* Bathrooms */}
                                    {/* <div className="flex-1 min-w-[120px] bg-white rounded-lg p-3 shadow-sm"> */}
                                    {/* <div className="min-w-0 bg-white rounded-lg p-3 shadow-sm overflow-hidden"> */}
                                    <div className="min-w-0 bg-white rounded-lg p-3 shadow-sm">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                                <Bath className="h-4 w-4 text-blue-600" />
                                            </div>
                                        </div>

                                        {readOnly ? (
                                            <p className="text-sm sm:text-base md:text-lg font-bold leading-tight text-gray-900 break-words">
                                                {localData.bathrooms}
                                            </p>
                                        ) : (
                                            <input
                                                type="number"
                                                value={localData.bathrooms || ""}
                                                onChange={(e) =>
                                                    handleInputChange("bathrooms", e.target.value)
                                                }
                                                className="text-lg font-bold text-gray-900 bg-transparent border-none outline-none w-full whitespace-nowrap [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                placeholder="0"
                                                min="0"
                                            />
                                        )}

                                        <p className="text-xs text-gray-500">Baths</p>
                                    </div>

                                    {/* Sq Ft */}
                                    {/* <div className="flex-1 min-w-[120px] bg-white rounded-lg p-3 shadow-sm"> */}
                                    {/* <div className="min-w-0 bg-white rounded-lg p-3 shadow-sm overflow-hidden"> */}
                                    <div className="min-w-0 bg-white rounded-lg p-3 shadow-sm">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                                <Square className="h-4 w-4 text-blue-600" />
                                            </div>
                                        </div>

                                        {readOnly ? (
                                            <p className="text-sm sm:text-base md:text-lg font-bold leading-tight text-gray-900 break-words">
                                                {(localData.area || 0).toLocaleString()}
                                            </p>
                                        ) : (
                                            <input
                                                type="number"
                                                value={localData.area || ""}
                                                onChange={(e) =>
                                                    handleInputChange("area", e.target.value)
                                                }
                                                className="text-base sm:text-lg font-bold leading-tight text-gray-900 bg-transparent border-none outline-none w-full whitespace-nowrap min-w-0 [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                placeholder="0"
                                                min="0"
                                            />
                                        )}

                                        <p className="text-xs text-gray-500">Sq Ft</p>
                                    </div>
                                </div>
                            </div>

                            {/* Website Link Field - Only shown in edit mode */}
                            {!readOnly && (
                                <div className="mb-4">
                                    <div className="flex items-start gap-2">
                                        <Globe className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" />
                                        <div className="flex-1">
                                            <input
                                                type="url"
                                                value={localData.website}
                                                onChange={(e) => handleInputChange("website", e.target.value)}
                                                className="font-medium text-gray-900 bg-transparent border-b border-gray-200 outline-none w-full focus:border-blue-500 pb-1 placeholder-gray-400"
                                                placeholder="Website / Listing Link (https://...)"
                                            />
                                            <p className="text-xs text-gray-400 mt-1">
                                                This link will open in a new tab when "View Listing" is clicked
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Website Link Display - Only shown in read mode if exists */}
                            {/* {readOnly && localData.website && (
                            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <Globe className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                    <span className="text-sm text-gray-600">External Link:</span>
                                    <a
                                        href={localData.website.startsWith('http') ? localData.website : `https://${localData.website}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate flex items-center gap-1"
                                    >
                                        {localData.website}
                                        <ExternalLink className="h-3 w-3" />
                                    </a>
                                </div>
                            </div>
                        )} */}
                        </div>

                        {/* Action Buttons */}
                        {viewListingHref ? (
                            <div className="template-preview-actions mt-4 flex gap-3">
                                <a
                                    href={viewListingHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3 text-sm font-semibold text-white hover:from-blue-600 hover:to-blue-700 transition"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    View Listing

                                </a>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MultiTemplate;
